import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  depotInjectionCharts: [],
  currentDepotInjectionChart: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Depot Injection Charts
export const fetchDepotInjectionCharts = createAsyncThunk(
  "depotInjectionCharts/fetchDepotInjectionCharts",
  async (_, { rejectWithValue }) => {
    try {
      // const response = await axios.get(`${API_URL}/depot-injection-charts`, {
      const response = await api.get('/depot-injection-charts');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          "Failed to fetch Depot Injection Charts"
      );
    }
  }
);

// Fetch single Depot Injection Chart by ID
export const fetchDepotInjectionChart = createAsyncThunk(
  "depotInjectionCharts/fetchDepotInjectionChart",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/depot-injection-charts/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          "Failed to fetch Depot Injection Chart"
      );
    }
  }
);

// Create Depot Injection Chart
export const createDepotInjectionChart = createAsyncThunk(
  "depotInjectionCharts/createDepotInjectionChart",
  async (chartData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        '/depot-injection-charts',
        chartData);
      return response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to create Depot Injection Chart"
      );
    }
  }
);

// Update Depot Injection Chart
export const updateDepotInjectionChart = createAsyncThunk(
  "depotInjectionCharts/updateDepotInjectionChart",
  async ({ case_id, chartData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/depot-injection-charts/case/${case_id}`,
        chartData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to update Depot Injection Chart"
      );
    }
  }
);

// Delete Depot Injection Chart
export const deleteDepotInjectionChart = createAsyncThunk(
  "depotInjectionCharts/deleteDepotInjectionChart",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/depot-injection-charts/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete Depot Injection Chart"
      );
    }
  }
);

// ==================== Slice ====================
const depotInjectionChartsSlice = createSlice({
  name: "depotInjectionCharts",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentDepotInjectionChart: (state, action) => {
      state.currentDepotInjectionChart = action.payload;
    },
    clearCurrentDepotInjectionChart: (state) => {
      state.currentDepotInjectionChart = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchDepotInjectionCharts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDepotInjectionCharts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.depotInjectionCharts = action.payload.data || [];;
      })
      .addCase(fetchDepotInjectionCharts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchDepotInjectionChart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDepotInjectionChart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDepotInjectionChart = action.payload;
      })
      .addCase(fetchDepotInjectionChart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createDepotInjectionChart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDepotInjectionChart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.depotInjectionCharts.unshift(action.payload);
      })
      .addCase(createDepotInjectionChart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateDepotInjectionChart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDepotInjectionChart.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.depotInjectionCharts.findIndex(
          (item) => item.id === updated.id
        );
        if (index !== -1) {
          state.depotInjectionCharts[index] = updated;
        }

        if (state.currentDepotInjectionChart?.id === updated.id) {
          state.currentDepotInjectionChart = updated;
        }
      })
      .addCase(updateDepotInjectionChart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteDepotInjectionChart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteDepotInjectionChart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.depotInjectionCharts = state.depotInjectionCharts.filter(
          (item) => item.id !== action.payload
        );

        if (state.currentDepotInjectionChart?.id === action.payload) {
          state.currentDepotInjectionChart = null;
        }
      })
      .addCase(deleteDepotInjectionChart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentDepotInjectionChart,
  clearCurrentDepotInjectionChart,
} = depotInjectionChartsSlice.actions;

export default depotInjectionChartsSlice.reducer;
